import { gql, DocumentNode } from "@apollo/client";
import {
  DATASOURCE_DEFINITION_NODE_FRAGMENT,
  DATASOURCES_FAST_INFO_NODE_FRAGMENT,
  DATASOURCES_SLOW_INFO_NODE_FRAGMENT,
} from "./shared";

export const GET_DATASOURCES_INSTANCES: DocumentNode = gql`
  query GetDatasourcesInstances {
    datasources(is_threat_intelligence: false) {
      edges {
        node {
          id
          name
          datasource_id
          friendly_name
          deleted
          enabled
          datasource_definition {
            id
            name
            category
            status
          }
        }
      }
    }
  }
`;

export const GET_INSTANCE_DETAILS: DocumentNode = gql`
  query GetInstanceDetails(
    $id: String!
    $with_collection_start_time: Boolean = true
  ) {
    datasources(filters: { datasource_ids: [$id] }) {
      edges {
        node {
          id
          name
          last_collection_time @include(if: $with_collection_start_time)
          check_connection_status
        }
      }
    }
  }
`;

export const GET_DATA_SOURCE_DEFINITIONS: DocumentNode = gql`
  ${DATASOURCE_DEFINITION_NODE_FRAGMENT}
  query GetDataSourceDefinitions(
    $with_last_received: Boolean!
    $with_tags: Boolean!
    $filters: DatasourceDefinitionsFilters
  ) {
    datasource_definitions(
      filters: $filters
      with_last_received: $with_last_received
      is_threat_intelligence: false
    ) {
      edges {
        node {
          ...datasourceDefinitionNodeFragment
        }
      }
    }
  }
`;

export const UPDATE_DATASOURCE_DEFINITION: DocumentNode = gql`
  mutation UpdateDatasourceDefinition($id: String!, $instructions: String) {
    update_datasource_definition(id: $id, instructions: $instructions) {
      ok
    }
  }
`;

export const GET_DATA_SOURCES: DocumentNode = gql`
  query GetDataSources(
    $with_collection_state: Boolean!
    $filters: DatasourceFilters
    $with_collection_start_time: Boolean!
  ) {
    datasources(filters: $filters) {
      edges {
        node {
          id
          friendly_name
          last_collection_time @include(if: $with_collection_start_time)
          collection_state @include(if: $with_collection_state)
          required_fields_value
          secret_configuration_value
          enabled
          name
          check_connection_status
          cron
          rap_instance_id
          days_not_seen_to_declare_fixed
          datasource_definition {
            id
            name
            status
            source_display_name {
              source_name
            }
          }
        }
      }
    }
  }
`;

/** Variables for GET_DATA_SOURCES query */
export interface GetDataSourcesVariables {
  with_collection_state?: boolean;
  filters?: Record<string, unknown>;
  with_collection_start_time?: boolean;
}

/** Response data shape of GET_DATA_SOURCES (datasources.edges). */
export interface GetDataSourcesData {
  datasources?: {
    edges?: Array<{ node: Record<string, unknown> }>;
  };
}

export interface GenerateGetDataSourcesParams {
  variables?: GetDataSourcesVariables | null;
  result?: GetDataSourcesData | null;
  error?: Error | null;
}

export interface GenerateGetDataSourcesResult {
  request: {
    query: DocumentNode;
    variables: GetDataSourcesVariables | null;
  };
  result?: { data: GetDataSourcesData };
  error?: Error;
}

const EMPTY_GET_DATA_SOURCES_RESULT: { data: GetDataSourcesData } = {
  data: {},
};

export const generateGetDataSources = ({
  variables = null,
  result = null,
  error = null,
}: GenerateGetDataSourcesParams): GenerateGetDataSourcesResult => {
  const getDataSourcesMock: GenerateGetDataSourcesResult = {
    request: {
      query: GET_DATA_SOURCES,
      variables,
    },
  };

  if (error) {
    return {
      ...getDataSourcesMock,
      error,
    };
  }

  if (result) {
    return {
      ...getDataSourcesMock,
      result: { data: result },
    };
  }

  return {
    ...getDataSourcesMock,
    result: EMPTY_GET_DATA_SOURCES_RESULT,
  };
};

export const ADD_DATA_SOURCE: DocumentNode = gql`
  mutation AddDatasource(
    $friendly_name: String!
    $datasource_definitions_id: String!
    $incoming_configuration: JSONString!
    $cron: String
    $enabled: Boolean
    $rap_instance_id: Int
    $days_not_seen_to_declare_fixed: Int
    $last_collected_threshold: Int
    $enable_last_collected_threshold: Boolean
    $enable_last_reported_threshold: Boolean
  ) {
    add_datasource(
      friendly_name: $friendly_name
      datasource_definitions_id: $datasource_definitions_id
      incoming_configuration: $incoming_configuration
      cron: $cron
      enabled: $enabled
      rap_instance_id: $rap_instance_id
      days_not_seen_to_declare_fixed: $days_not_seen_to_declare_fixed
      last_reported_threshold: $days_not_seen_to_declare_fixed
      last_collected_threshold: $last_collected_threshold
      enable_last_collected_threshold: $enable_last_collected_threshold
      enable_last_reported_threshold: $enable_last_reported_threshold
    ) {
      ok
      msg
      datasource_id
    }
  }
`;

export const UPDATE_DATA_SOURCE: DocumentNode = gql`
  mutation UpdateDatasource(
    $id: String!
    $enabled: Boolean
    $friendly_name: String
    $cron: String
    $incoming_configuration: JSONString
    $rap_instance_id: Int
    $days_not_seen_to_declare_fixed: Int
    $last_collected_threshold: Int
    $enable_last_collected_threshold: Boolean
    $enable_last_reported_threshold: Boolean
  ) {
    update_datasource(
      id: $id
      enabled: $enabled
      friendly_name: $friendly_name
      incoming_configuration: $incoming_configuration
      cron: $cron
      rap_instance_id: $rap_instance_id
      days_not_seen_to_declare_fixed: $days_not_seen_to_declare_fixed
      last_reported_threshold: $days_not_seen_to_declare_fixed
      last_collected_threshold: $last_collected_threshold
      enable_last_collected_threshold: $enable_last_collected_threshold
      enable_last_reported_threshold: $enable_last_reported_threshold
    ) {
      ok
      msg
    }
  }
`;

export const LAUNCH_COLLECTION_ACTION: DocumentNode = gql`
  mutation launch_collection_action($subject_id: String!) {
    launch_collection_action(subject_id_: $subject_id) {
      task_id
    }
  }
`;

export const CHECK_DATASOURCE_CONNECTION: DocumentNode = gql`
  mutation check_datasource_connection($datasource_id: String) {
    check_datasource_connection(datasource_id: $datasource_id) {
      status
      msg
      task_id
    }
  }
`;

export const GET_INTEGRATION_STATISTICS: DocumentNode = gql`
  query get_integration_statistics($filters: IntegrationStatisticsFilters) {
    get_integration_statistics(filters: $filters) {
      edges {
        node {
          type
          date
          collected_count_success
          event_level
          event_timestamp
          event_user_message
          event_user_type
        }
      }
    }
  }
`;

export const GET_DATA_SOURCES_FAST_INFO: DocumentNode = gql`
  ${DATASOURCES_FAST_INFO_NODE_FRAGMENT}
  query GetDataSourcesFastInfo {
    datasources(is_threat_intelligence: false) {
      edges {
        node {
          ...datasourcesFastInfoNodeFragment
        }
      }
    }
  }
`;

export const GET_DATA_SOURCES_SLOW_INFO: DocumentNode = gql`
  ${DATASOURCES_SLOW_INFO_NODE_FRAGMENT}
  query GetDataSourcesSlowInfo(
    $with_collection_state: Boolean = true
    $with_collection_start_time: Boolean = true
  ) {
    datasources(is_threat_intelligence: false) {
      edges {
        node {
          ...datasourcesSlowInfoNodeFragment
        }
      }
    }
  }
`;
